/**
 * Stream Intelligence — post-stream P&L review
 * Design: the portal design system (src/design/README.md); page styles in StreamIntelligence.css
 */
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Page, PageHeader } from 'design';
import { IconArrowLeft, IconSearch, IconChevronRight, IconX, IconChevronLeft, IconHistory } from 'design/icons';
import './StreamIntelligence.css';

// Every read goes through the portal's server (api/stream-intelligence), which holds the database and
// screenshot keys; the browser sends only the staff sign-in. Resolves to null when a read fails.
async function intel(view, params = {}) {
  let user = null;
  try { user = JSON.parse(window.localStorage.getItem('user') || 'null'); } catch (error) { user = null; }
  if (!user || !user.accessToken) return null;
  const search = new URLSearchParams({ view });
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  });
  try {
    const response = await fetch(`/api/stream-intelligence?${search.toString()}`, { headers: { Authorization: `Bearer ${user.accessToken}` } });
    return response.ok ? await response.json() : null;
  } catch (error) {
    return null;
  }
}

// ─── Constants ────────────────────────────────────────────────────────────────
const GRADE_COST = 8.33;

const GRADE_SCORES = {
  'GEM MINT': 10, 'GEM-MINT': 10,
  'MINT': 9,
  'NEAR MINT-MINT': 8, 'NM-MT': 8,
  'NEAR MINT': 7, 'NM': 7,
  'EXCELLENT-MINT': 6, 'EX-MT': 6,
  'EXCELLENT': 5,
  'VERY GOOD-EXCELLENT': 4, 'VG-EX': 4,
  'VERY GOOD': 3, 'GOOD': 2, 'POOR': 1,
};

const SELLERS = { randolph: 'Randolph', randhits: 'RandHits', randbreaks: 'RandBreaks', layla: 'Layla', amelie: 'Amelie', brad: 'Brad', radu: 'Radu', pat: 'Pat' };
const STREAMERS = { 1: 'Unassigned', 2: 'Pat', 3: 'Radu', 4: 'Layla', 5: 'Randolph', 6: 'Brad', 7: 'CeCe', 8: 'Torin', 9: 'Adam' };
const CATEGORY_NAMES = { 1: 'Unassigned', 2: 'Singles', 3: 'Slabs', 4: 'Breaks', 5: 'Surprise Set', 6: 'Singles & Slabs' };
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const aceImgUrl = (cert, ci) => {
  const c = cert || ((ci || '').match(/\[CERT:(\d+)\]/) || [])[1];
  return c ? `https://collectible-images.ams3.cdn.digitaloceanspaces.com/public/collectible/${c}/${c}_front.webp` : null;
};

const calcProfit = (h, c, type) => {
  const gross = h - c;
  return +((gross - h * 0.06 - (h * 0.0242 + 0.25) - (type === 'slab' ? GRADE_COST : 0) - Math.max(0, gross / 6)).toFixed(2));
};

const gbp  = (n, sign = false) => !isFinite(n) ? '—' : (sign ? (n >= 0 ? '+' : '-') : n < 0 ? '-' : '') + '£' + Math.abs(n).toFixed(2);
const pct  = n => !isFinite(n) ? '—' : (n >= 0 ? '+' : '') + n.toFixed(1) + '%';
// Profit and margin colours are status: green good, amber thin, red loss; grey when there is no cost.
const pCol = n => n === null ? '#666666' : n < 0 ? '#E42545' : '#37BE7A';
const mCol = n => !isFinite(n) || n === null ? '#666666' : n < 0 ? '#E42545' : n < 15 ? '#E6A433' : n < 25 ? '#E6A433' : '#37BE7A';

const gradeStr = g => {
  if (!g) return null;
  const score = GRADE_SCORES[g.toUpperCase().trim()];
  return score ? `ACE ${score}` : g;
};

const fmtDate = d => {
  if (!d) return d;
  const dt = new Date(d + 'T12:00:00');
  return `${DAYS[dt.getDay()]} ${dt.getDate()} ${MONTHS[dt.getMonth()]}`;
};

const streamKind = (slabs, singles, categoryId) => {
  if (categoryId && CATEGORY_NAMES[categoryId]) return CATEGORY_NAMES[categoryId];
  return slabs > 0 && singles > 0 ? 'Mixed' : slabs > 0 ? 'Slabs' : singles > 0 ? 'Singles' : 'Breaks';
};

const stats = rows => {
  let rev = 0, th = 0, tc = 0, tp = 0, tracked = 0, noData = 0;
  for (const r of rows) {
    const h = parseFloat(r.hammer_gbp) || 0;
    rev += h;
    const cost = r.buy_price ?? r.cost_price_gbp ?? null;
    if (cost !== null) {
      const p = calcProfit(h, parseFloat(cost), r.item_type);
      th += h; tc += parseFloat(cost); tp += p; tracked++;
    } else noData++;
  }
  return { rev, th, tc, tp, tracked, noData, total: rows.length, margin: th > 0 ? (tp / th) * 100 : null };
};

// ─── Lightbox ─────────────────────────────────────────────────────────────────
function Lightbox({ src, onClose }) {
  if (!src) return null;
  return (
    <div onClick={onClose} className="si-lightbox">
      <img src={src} alt="" onClick={onClose} />
    </div>
  );
}

// ─── Stat tile ────────────────────────────────────────────────────────────────
function Tile({ label, value, color, sub }) {
  return (
    <div className="rc-stat si-tile">
      <div className="rc-stat__label">{label}</div>
      <div className="rc-stat__value" style={color ? { color } : undefined}>{value}</div>
      {sub && <div className="rc-stat__note">{sub}</div>}
    </div>
  );
}

// ─── Stream list ──────────────────────────────────────────────────────────────
function StreamList({ onSelect }) {
  const [list, setList]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [streamerFilter, setStreamerFilter] = useState('all');

  useEffect(() => {
    // The server sends the last 90 days of sales for the list (the full history is too slow),
    // and the stream uploads for category labels
    intel('streams').then((body) => {
      const data = (body && body.screenshots) || [];
      const uploads = body ? body.uploads : null;
      if (!data) { setLoading(false); return; }
      // Map upload UUIDs → upload for accurate stream type labels
      const uploadsByUUID = {};
      // Store ALL uploads per streamer+date (array) for item-type-aware fallback
      const uploadsByStreamerDate = {};
      const SLAB_CATS_SET  = new Set([3, 6]);
      const SINGLE_CATS_SET = new Set([2, 6]);
      for (const u of (uploads || [])) {
        const m = (u.file_name || '').match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
        if (m) uploadsByUUID[m[0].toLowerCase()] = u;
        const dk = `${u.stream_date}__${u.streamer_id}`;
        if (!uploadsByStreamerDate[dk]) uploadsByStreamerDate[dk] = [];
        uploadsByStreamerDate[dk].push(u);
      }
      const map = {};
      for (const r of data) {
        const sid = r.streamer_id || null;
        const k = `${r.stream_date}__${r.seller_username || 'unknown'}__${sid || 'x'}`;
        if (!map[k]) map[k] = { date: r.stream_date, seller: r.seller_username || 'unknown', streamer_id: sid, slabs: 0, singles: 0, break_category_id: null };
        if (r.item_type === 'slab') map[k].slabs++; else if (r.item_type === 'single') map[k].singles++;
        // Resolve category from upload UUID, then item-type-aware streamer+date fallback
        if (!map[k].break_category_id) {
          let upload = null;
          if (r.stream_id) {
            const uuid = (r.stream_id.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i) || [])[0]?.toLowerCase();
            if (uuid) upload = uploadsByUUID[uuid];
          }
          if (!upload && sid) {
            const candidates = uploadsByStreamerDate[`${r.stream_date}__${sid}`] || [];
            const validCats = r.item_type === 'slab' ? SLAB_CATS_SET : r.item_type === 'single' ? SINGLE_CATS_SET : null;
            // Prefer a category that matches the item type; fall back to first
            upload = (validCats ? candidates.find(u => validCats.has(u.break_category_id)) : null) || candidates[0] || null;
          }
          if (upload) map[k].break_category_id = upload.break_category_id;
        }
      }
      setList(Object.values(map).sort((a, b) => b.date.localeCompare(a.date)));
      setLoading(false);
    });
  }, []);

  // Unique streamers present in data
  const streamerIds = useMemo(() => {
    const ids = [...new Set(list.map(s => s.streamer_id).filter(Boolean))];
    return ids.sort((a, b) => a - b);
  }, [list]);

  const filtered = useMemo(() =>
    streamerFilter === 'all' ? list : list.filter(s => String(s.streamer_id) === streamerFilter),
    [list, streamerFilter]
  );

  if (loading) return <p className="si-loading">Loading…</p>;

  return (
    <div className="si-streams">
      {/* Streamer filter */}
      <div className="si-filter">
        <span className="rc-label">Streamer</span>
        {[['all', 'All']].concat(streamerIds.map(id => [String(id), STREAMERS[id] || `#${id}`])).map(([val, label]) => (
          <button key={val} onClick={() => setStreamerFilter(val)} className={`rc-chip${streamerFilter === val ? ' is-active' : ''}`}>{label}</button>
        ))}
        <span className="si-count">{filtered.length} streams</span>
      </div>

      <div className="si-stream-list">
        {filtered.map((s, i) => {
          const kind = streamKind(s.slabs, s.singles, s.break_category_id);
          const streamerName = s.streamer_id ? (STREAMERS[s.streamer_id] || `#${s.streamer_id}`) : null;
          return (
            <div key={i} onClick={() => onSelect(s)} className="rc-tile si-stream">
              <div className="si-stream__main">
                {/* Streamer strip */}
                {s.streamer_id && <div className="si-stream__strip" />}
                {/* Date */}
                <div className="si-stream__date">
                  <div className="si-stream__day">{fmtDate(s.date)}</div>
                  <div className="si-stream__iso">{s.date}</div>
                </div>
                {/* Tags */}
                <div className="si-stream__tags">
                  <span className="rc-badge si-kind">{kind}</span>
                  {streamerName && (
                    <span className="rc-badge si-streamer">{streamerName}</span>
                  )}
                  <span className="rc-badge si-account">{SELLERS[s.seller] || s.seller}</span>
                </div>
              </div>
              <div className="si-stream__end">
                <span>{s.slabs + s.singles} cards{s.slabs > 0 ? ` · ${s.slabs} slabs` : ''}{s.singles > 0 ? ` · ${s.singles} singles` : ''}</span>
                <IconChevronRight size={16} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Card Modal ──────────────────────────────────────────────────────────────
function CardModal({ card, allCards, onClose }) {
  const [current, setCurrent]     = useState(card);
  const [history, setHistory]     = useState([]);
  const [loadingHist, setLoadHist] = useState(false);
  const [lb, setLb]               = useState(null);
  const [gradeFilter, setGradeFilter] = useState(current.grade || null);

  const idx     = allCards.findIndex(c => c.id === current.id);
  const hasPrev = idx > 0;
  const hasNext = idx < allCards.length - 1;
  const goTo    = useCallback(c => { setCurrent(c); setHistory([]); }, []);
  const goPrev  = () => hasPrev && goTo(allCards[idx - 1]);
  const goNext  = () => hasNext && goTo(allCards[idx + 1]);

  // Preload adjacent card images so navigating feels instant
  useEffect(() => {
    const toPreload = [];
    [-1, 1].forEach(offset => {
      const neighbour = allCards[idx + offset];
      if (!neighbour) return;
      if (neighbour._ace)   toPreload.push(neighbour._ace);
      if (neighbour._thumb) toPreload.push(neighbour._thumb);
    });
    toPreload.forEach(src => { const img = new window.Image(); img.src = src; });
  }, [current.id]);

  useEffect(() => {
    const handler = e => {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); goPrev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // Reset grade filter when navigating to a new card
  useEffect(() => { setGradeFilter(current.grade || null); }, [current.id]);

  useEffect(() => {
    const name = current.pokemon_name;
    const num  = current.card_number;
    if (!name && !num) return;
    setLoadHist(true);
    // Slabs stay with slabs and JP with JP; the server matches the card number, else the name
    intel('history', { itemType: current.item_type, cardNumber: num, name, language: current.language })
      .then((body) => { setHistory((body && body.rows) || []); setLoadHist(false); });
  }, [current.id]);

  // Unique grades present in history for filter buttons
  const gradeOptions = useMemo(() => {
    const seen = new Set();
    history.forEach(h => { if (h.grade) seen.add(h.grade.toUpperCase().trim()); });
    return [...seen].sort();
  }, [history]);

  // History rows shown in table (filtered by active grade button)
  const displayHistory = useMemo(() => {
    if (!gradeFilter) return history;
    return history.filter(h => (h.grade || '').toUpperCase().trim() === gradeFilter.toUpperCase().trim());
  }, [history, gradeFilter]);

  // Averages computed from displayed (grade-filtered) rows
  const avgStats = useMemo(() => {
    if (!displayHistory.length) return null;
    const hammers = displayHistory.map(h => parseFloat(h.hammer_gbp) || 0).filter(h => h > 0);
    const costed  = displayHistory.map(h => {
      const hCost = h.buy_price ?? h.cost_price_gbp ?? null;
      const hH    = parseFloat(h.hammer_gbp) || 0;
      if (hCost == null || hH === 0) return null;
      const p = calcProfit(hH, parseFloat(hCost), h.item_type);
      return { profit: p, margin: (p / hH) * 100 };
    }).filter(Boolean);
    return {
      count:        displayHistory.length,
      totalHistory: history.length,
      gradeLabel:   gradeStr(gradeFilter) || null,
      gradeFiltered: !!gradeFilter && displayHistory.length < history.length,
      avgHammer:    hammers.length ? hammers.reduce((a, b) => a + b, 0) / hammers.length : null,
      bestHammer:   hammers.length ? Math.max(...hammers) : null,
      worstHammer:  hammers.length ? Math.min(...hammers) : null,
      avgProfit:    costed.length ? costed.reduce((a, b) => a + b.profit, 0) / costed.length : null,
      avgMargin:    costed.length ? costed.reduce((a, b) => a + b.margin, 0) / costed.length : null,
      costedCount:  costed.length,
    };
  }, [displayHistory]);

  const grade    = gradeStr(current.grade);
  const hasAce   = !!current._ace;
  const hasThumb = !!current._thumb;
  const streamerName  = current.streamer_id ? (STREAMERS[current.streamer_id] || `#${current.streamer_id}`) : null;

  return (
    <div onClick={onClose} className="si-overlay">
      {lb && (
        <div onClick={e => { e.stopPropagation(); setLb(null); }} className="si-lightbox">
          <img src={lb} alt="" />
        </div>
      )}
      <div onClick={e => e.stopPropagation()} className="si-dialog">

        {/* Header */}
        <div className="si-dialog__head">
          <div className="si-dialog__nav">
            <button onClick={goPrev} disabled={!hasPrev} className="rc-btn rc-btn--icon rc-btn--sm">
              <IconChevronLeft size={16} />
            </button>
            <span className="si-dialog__count">{idx + 1} / {allCards.length}</span>
            <button onClick={goNext} disabled={!hasNext} className="rc-btn rc-btn--icon rc-btn--sm">
              <IconChevronRight size={16} />
            </button>
          </div>
          <button onClick={onClose} className="rc-btn rc-btn--icon rc-btn--sm rc-btn--ghost">
            <IconX size={16} />
          </button>
        </div>

        {/* Card detail — stacks on mobile */}
        <div className="si-dialog__card">
          {/* Images — keyed by card id so React always creates a fresh element on navigate */}
          {(hasAce || hasThumb) && (
            <div className="si-dialog__media">
              {hasAce && <img loading="lazy" key={current.id + '-ace'} src={current._ace} alt="" onClick={() => setLb(current._ace)} className="si-dialog__ace" onError={e => e.target.style.opacity = 0} />}
              {hasThumb && <img loading="lazy" key={current.id + '-thumb'} src={current._thumb} alt="" onClick={() => setLb(current._shot || current._thumb)} className="si-dialog__shot" onError={e => e.target.style.opacity = 0} />}
            </div>
          )}
          {/* Details */}
          <div className="si-dialog__info">
            <div className="si-dialog__name">
              {current._name || <span className="si-unknown">Unknown</span>}
            </div>
            <div className="si-meta">
              {[current.card_number, current.set_name].filter(Boolean).join(' · ')}
              {grade && <span className="si-grade">{grade}</span>}
              {current.language === 'Japanese' && <span className="si-flag">🇯🇵</span>}
            </div>
            {/* This sale P&L */}
            <div className="si-pl">
              {[['Hammer', `£${parseFloat(current.hammer_gbp || 0).toFixed(2)}`, '#FFFFFF'],
                ['Cost',   current._cost != null ? `£${parseFloat(current._cost).toFixed(2)}` : '—', '#9B9B9B'],
                ['Profit', current._profit != null ? gbp(current._profit, true) : '—', pCol(current._profit)],
                ['Margin', current._margin != null ? pct(current._margin) : '—', mCol(current._margin)],
              ].map(([label, val, color]) => (
                <div key={label}>
                  <div className="rc-label">{label}</div>
                  <div className="si-pl__value" style={{ color }}>{val}</div>
                </div>
              ))}
            </div>
            {/* Streamer / account / date */}
            <div className="si-dialog__tags">
              {streamerName && <span className="rc-badge si-streamer">{streamerName}</span>}
              <span className="rc-badge si-account">{SELLERS[current.seller_username] || current.seller_username}</span>
              <span className="si-muted">{fmtDate(current.stream_date)}</span>
              {current.sold_at && <span className="si-muted">{new Date(current.sold_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>}
            </div>
          </div>
        </div>

        {/* Average performance strip */}
        {avgStats && avgStats.count >= 1 && (
          <div className="si-avg">
            <span className="rc-label si-avg__label">
              {avgStats.gradeFiltered
                ? <>Avg for <span className="si-avg__grade">{avgStats.gradeLabel}</span> · {avgStats.count} of {avgStats.totalHistory} sales</>
                : <>Avg across {avgStats.count} sales</>}
            </span>
            <div className="si-avg__figures">
              {avgStats.avgHammer != null && (
                <span>Hammer <strong>£{avgStats.avgHammer.toFixed(0)}</strong></span>
              )}
              {avgStats.avgProfit != null && (
                <span>Avg profit <strong style={{ color: pCol(avgStats.avgProfit) }}>{gbp(avgStats.avgProfit, true)}</strong></span>
              )}
              {avgStats.avgMargin != null && (
                <span>Avg margin <strong style={{ color: mCol(avgStats.avgMargin) }}>{pct(avgStats.avgMargin)}</strong></span>
              )}
              {avgStats.bestHammer != null && (
                <span>Best <strong style={{ color: '#37BE7A' }}>£{avgStats.bestHammer.toFixed(0)}</strong></span>
              )}
              {avgStats.worstHammer != null && (
                <span>Worst <strong style={{ color: '#E42545' }}>£{avgStats.worstHammer.toFixed(0)}</strong></span>
              )}
              {avgStats.costedCount > 0 && avgStats.costedCount < avgStats.count && (
                <span className="si-muted">{avgStats.costedCount}/{avgStats.count} costed</span>
              )}
            </div>
          </div>
        )}

        {/* Sale history */}
        <div className="si-history">
          <div className="si-history__head">
            <IconHistory size={16} />
            <span className="si-history__title">Sale History</span>
            {!loadingHist && history.length > 0 && <span className="si-muted">{history.length} records</span>}
          </div>

          {/* Grade filter buttons */}
          {!loadingHist && gradeOptions.length > 1 && (
            <div className="si-chips">
              <button onClick={() => setGradeFilter(null)} className={!gradeFilter ? 'is-active' : undefined}>
                All
              </button>
              {gradeOptions.map(g => {
                const active = gradeFilter && gradeFilter.toUpperCase().trim() === g;
                return (
                  <button key={g} onClick={() => setGradeFilter(active ? null : g)} className={active ? 'is-active' : undefined}>
                    {gradeStr(g) || g}
                  </button>
                );
              })}
            </div>
          )}

          {loadingHist && <p className="si-note">Loading…</p>}
          {!loadingHist && history.length === 0 && <p className="si-note">No history found.</p>}
          {!loadingHist && history.length > 0 && displayHistory.length === 0 && <p className="si-note">No records for this grade.</p>}

          {!loadingHist && displayHistory.length > 0 && (
            <div className="si-htable">
              <div className="si-htable__inner">
                <div className="si-hrow si-hrow--head">
                  {['Date', 'Streamer', 'Account', 'Type', 'Hammer', 'Profit', 'Margin'].map(h => (
                    <div key={h} className="rc-label">{h}</div>
                  ))}
                </div>
                {displayHistory.map((h, i) => {
                  const isCurrent = h.id === current.id;
                  const hCost   = h.buy_price ?? h.cost_price_gbp ?? null;
                  const hH      = parseFloat(h.hammer_gbp) || 0;
                  const hProfit = hCost != null ? calcProfit(hH, parseFloat(hCost), h.item_type) : null;
                  const hMargin = hProfit != null && hH > 0 ? (hProfit / hH) * 100 : null;
                  const hSid    = h.streamer_id;
                  const hGrade  = gradeStr(h.grade);
                  const inStream = allCards.find(c => c.id === h.id);
                  return (
                    <div key={h.id || i}
                      onClick={() => inStream && goTo(inStream)}
                      className={`si-hrow${isCurrent ? ' is-current' : ''}${inStream ? ' is-link' : ''}`}
                    >
                      <div className="si-muted">{fmtDate(h.stream_date)}</div>
                      <div>
                        <span className="si-hrow__streamer">{STREAMERS[hSid] || `#${hSid}`}</span>
                        {hGrade && <div className="si-grade si-grade--block">{hGrade}</div>}
                      </div>
                      <div className="si-muted">{SELLERS[h.seller_username] || h.seller_username}</div>
                      <div className="si-muted">{h.item_type === 'slab' ? 'Slab' : 'Single'}</div>
                      <div className="si-hrow__hammer">£{hH.toFixed(0)}</div>
                      <div className="si-hrow__profit" style={{ color: pCol(hProfit) }}>{hProfit != null ? gbp(hProfit, true) : <span className="si-dash">—</span>}</div>
                      <div style={{ color: mCol(hMargin) }}>{hMargin != null ? pct(hMargin) : <span className="si-dash">—</span>}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Stream detail ────────────────────────────────────────────────────────────
function StreamDetail({ stream, onBack }) {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [tab, setTab]           = useState('all');
  const [lb, setLb]             = useState(null);
  const [modalCard, setModalCard] = useState(null);

  useEffect(() => {
    intel('stream', { date: stream.date, seller: stream.seller, streamerId: stream.streamer_id })
      .then((body) => { setRows((body && body.rows) || []); setLoading(false); });
  }, [stream]);

  const enriched = useMemo(() =>
    rows
      .filter(r => r.pokemon_name || r.card_identified || r.screenshot_path || r.buy_price || r.cost_price_gbp)
      .map((r, idx) => {
        const cost   = r.buy_price ?? r.cost_price_gbp ?? null;
        const h      = parseFloat(r.hammer_gbp) || 0;
        const profit = cost !== null ? calcProfit(h, parseFloat(cost), r.item_type) : null;
        const margin = profit !== null && h > 0 ? (profit / h) * 100 : null;
        const name   = r.pokemon_name || (r.card_identified ? r.card_identified.replace(/\s*ACE\s.*/,'').replace(/\s*[·].*/,'').replace(/\s*\[.*/,'').trim() : null);
        return { ...r, _n: idx + 1, _cost: cost, _profit: profit, _margin: margin, _name: name,
          _thumb: r.screenshot_src || null, _shot: r.screenshot_src || null,
          _ace: aceImgUrl(r.cert_number, r.card_identified) };
      }), [rows]);

  const slabs   = useMemo(() => enriched.filter(r => r.item_type === 'slab'), [enriched]);
  const singles = useMemo(() => enriched.filter(r => r.item_type !== 'slab'), [enriched]);
  const hasBoth = slabs.length > 0 && singles.length > 0;
  const base    = tab === 'slabs' ? slabs : tab === 'singles' ? singles : enriched;

  const display = useMemo(() => {
    if (!search) return base;
    const q = search.toLowerCase();
    return base.filter(r => [r.pokemon_name, r.card_number, r.set_name, r.listing_title].some(f => (f || '').toLowerCase().includes(q)));
  }, [base, search]);

  const S  = stats(base);
  const SS = stats(singles);
  const SL = stats(slabs);

  return (
    <>
      {modalCard && <CardModal card={modalCard} allCards={display} onClose={() => setModalCard(null)} />}
      <Lightbox src={lb} onClose={() => setLb(null)} />

      {/* Page header */}
      <div className="si-detail-head">
        <button className="si-back" onClick={onBack}>
          <IconArrowLeft size={16} /> All Streams
        </button>
        <div className="si-detail-title">
          <div className="si-detail-eyebrow">
            <div className="rc-eyebrow">{SELLERS[stream.seller] || stream.seller}</div>
            {stream.streamer_id && (
              <span className="rc-badge si-streamer">
                {STREAMERS[stream.streamer_id] || `#${stream.streamer_id}`}
              </span>
            )}
          </div>
          <h1 className="rc-page-title">{fmtDate(stream.date)}</h1>
          <p className="rc-page-description">{stream.date} · {streamKind(stream.slabs || slabs.length, stream.singles || singles.length, stream.break_category_id)} stream</p>
        </div>
      </div>

      {loading && <p className="si-loading">Loading…</p>}

      {!loading && (<>

        {/* Multi-type breakdown */}
        {hasBoth && tab === 'all' && (
          <div className="si-breakdown">
            {[['Singles', SS], ['Slabs', SL]].map(([label, s]) => (
              <div key={label} className="si-breakdown__row">
                <span className="rc-label">{label}</span>
                <span className="si-breakdown__rev">£{s.rev.toFixed(0)}</span>
                <span className="si-breakdown__profit" style={{ color: pCol(s.tp) }}>{gbp(s.tp, true)}</span>
                <span style={{ color: mCol(s.margin) }}>({pct(s.margin)})</span>
                <span className="si-muted">{s.tracked}/{s.total}</span>
              </div>
            ))}
          </div>
        )}

        {/* Stats — two rows */}
        <section className="si-stat-group">
          <div>
            <span className="rc-label">Cost-matched — {S.tracked} {base[0]?.item_type === 'slab' ? 'slabs' : 'cards'}</span>
          </div>
          <div className="si-stats">
            <Tile label="Hammer" value={`£${S.th.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`} />
            <Tile label="Cost"   value={S.tracked > 0 ? `£${S.tc.toFixed(0)}` : '—'} />
            <Tile label="Net Profit" value={S.tracked > 0 ? gbp(S.tp, true) : '—'} color={pCol(S.tp)} />
            <Tile label="Margin" value={S.tracked > 0 ? pct(S.margin) : '—'} color={mCol(S.margin)} />
          </div>
        </section>

        <section className="si-stat-group">
          <div>
            <span className="rc-label">Full stream — {S.total} {base[0]?.item_type === 'slab' ? 'slabs' : 'cards'}</span>
          </div>
          <div className="si-stats">
            <Tile label="Revenue" value={`£${S.rev.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`} />
            <Tile label="Avg Hammer" value={S.total > 0 ? `£${(S.rev / S.total).toFixed(0)}` : '—'} />
            <Tile label="No Cost Data" value={S.noData} color={S.noData > 0 ? '#E6A433' : '#37BE7A'} />
          </div>
        </section>

        {/* Controls */}
        <div className="si-toolbar">
          {hasBoth && (
            <div className="rc-segmented si-tabs">
              {[['all', `All (${enriched.length})`], ['slabs', `Slabs (${slabs.length})`], ['singles', `Singles (${singles.length})`]].map(([v, l]) => (
                <button key={v} onClick={() => setTab(v)} className={tab === v ? 'is-active' : undefined}>{l}</button>
              ))}
            </div>
          )}
          <div className="rc-input si-search">
            <IconSearch size={16} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search cards…" />
          </div>
          <span className="si-count">{display.length} cards</span>
        </div>

        {/* Table */}
        <div className="si-table">
          {/* Header */}
          <div className="si-row si-row--head">
            {['#', 'Slab', 'Shot', 'Card', 'Hammer', 'Cost', 'Profit'].map(h => (
              <div key={h} className="rc-label">{h}</div>
            ))}
          </div>
          {/* Streamer attribution label (shown when data has streamer info) */}
          {rows.length > 0 && rows[0]?.streamer_id && (() => {
            const sid = rows[0].streamer_id;
            return (
              <div className="si-attribution">
                <div className="si-attribution__dot" />
                <span className="si-attribution__name">{STREAMERS[sid] || `Streamer #${sid}`}</span>
                <span className="si-muted">· {rows.length} cards</span>
              </div>
            );
          })()}

          {/* Rows */}
          {display.map((r, i) => {
            const profitNeg = r._profit !== null && r._profit < 0;
            const grade     = gradeStr(r.grade);
            const hasAce    = !!r._ace;
            const hasThumb  = !!r._thumb;

            return (
              <div key={r.id || i} className={`si-row${profitNeg ? ' is-loss' : ''}`}>
                {/* # */}
                <div className="si-cell si-cell--n">{r._n}</div>

                {/* ACE slab */}
                <div onClick={hasAce ? () => setLb(r._ace) : undefined}
                  className={`si-cell si-media si-media--slab${hasAce ? ' has-image' : ''}`}>
                  {hasAce
                    ? <img loading="lazy" src={r._ace} alt="" onError={e => e.target.style.opacity = 0} />
                    : <span className="si-dash">—</span>}
                </div>

                {/* Screenshot */}
                <div onClick={hasThumb ? () => setLb(r._shot || r._thumb) : undefined}
                  className={`si-cell si-media si-media--shot${hasThumb ? ' has-image' : ''}`}>
                  {hasThumb && <img loading="lazy" src={r._thumb} alt="" onError={e => e.target.style.opacity = 0} />}
                </div>

                {/* Card identity — click to open modal */}
                <div className="si-cell si-card" onClick={() => setModalCard(r)}>
                  <div className="si-card__name">
                    {r._name || <span className="si-unknown">Unknown</span>}
                  </div>
                  <div className="si-meta si-card__meta">
                    {[r.card_number, r.set_name].filter(Boolean).join(' · ')}
                    {grade && <span className="si-grade">{grade}</span>}
                    {r.language === 'Japanese' && <span className="si-flag">🇯🇵</span>}
                  </div>
                  {r.listing_title && (
                    <div className="si-card__listing">
                      {r.listing_title}
                    </div>
                  )}
                  {(r._cost === null || profitNeg) && (
                    <div className="si-card__flags">
                      {r._cost === null && <span className="rc-badge rc-badge--warning">No cost</span>}
                      {profitNeg && <span className="rc-badge rc-badge--danger">Loss</span>}
                    </div>
                  )}
                </div>

                {/* Hammer */}
                <div className="si-cell si-num" data-label="Hammer">
                  <div className="si-hammer">
                    £{parseFloat(r.hammer_gbp).toFixed(0)}
                  </div>
                </div>

                {/* Cost */}
                <div className="si-cell si-num" data-label="Cost">
                  <div className="si-cost">
                    {r._cost !== null ? `£${parseFloat(r._cost).toFixed(2)}` : <span className="si-dash">—</span>}
                  </div>
                </div>

                {/* Profit */}
                <div className="si-cell si-num" data-label="Profit">
                  <div className="si-profit" style={{ color: pCol(r._profit) }}>
                    {r._profit !== null ? gbp(r._profit, true) : <span className="si-dash">—</span>}
                  </div>
                  {r._margin !== null && <div className="si-margin" style={{ color: mCol(r._margin) }}>{pct(r._margin)}</div>}
                </div>
              </div>
            );
          })}
        </div>

        {S.noData > 0 && (
          <div className="rc-alert rc-alert--warning si-nocost">
            ⚠️ {S.noData} cards without cost data · £{base.filter(r => r._cost === null).reduce((a, r) => a + (parseFloat(r.hammer_gbp) || 0), 0).toFixed(0)} untracked revenue
          </div>
        )}
      </>)}
    </>
  );
}


// ─── Sales Search ────────────────────────────────────────────────────────────
export function SalesSearch() {
  const [allData, setAllData]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [loadedCount, setLoadedCount] = useState(0);
  const [query, setQuery]             = useState('');
  const [typeFilter, setTypeFilter]   = useState('slab');
  const [streamerFilter, setStreamer]  = useState(null);
  const [sortBy, setSortBy]           = useState('recent');
  const [modalCard, setModalCard]     = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      const pages = [];
      let from = 0;
      while (true) {
        const body = await intel('sales', { from });
        const data = body && body.rows;
        if (!data || !data.length) break;
        pages.push(...data);
        setLoadedCount(pages.length);
        if (data.length < 1000) break;
        from += 1000;
      }
      setAllData(pages);
      setLoading(false);
    };
    load();
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const enriched = useMemo(() =>
    allData.map(r => {
      const cost   = r.buy_price ?? r.cost_price_gbp ?? null;
      const h      = parseFloat(r.hammer_gbp) || 0;
      const profit = cost !== null ? calcProfit(h, parseFloat(cost), r.item_type) : null;
      const margin = profit !== null && h > 0 ? (profit / h) * 100 : null;
      const name   = r.pokemon_name || (r.card_identified ? r.card_identified.replace(/\s*ACE\s.*/, '').replace(/\s*[·].*/, '').replace(/\s*\[.*/, '').trim() : null);
      return { ...r, _cost: cost, _profit: profit, _margin: margin, _name: name,
        _thumb: r.screenshot_src || null, _shot: r.screenshot_src || null,
        _ace: aceImgUrl(r.cert_number, r.card_identified) };
    }), [allData]);

  const streamerIds = useMemo(() =>
    [...new Set(enriched.map(r => r.streamer_id).filter(Boolean))].sort((a, b) => a - b),
    [enriched]);

  const results = useMemo(() => {
    let arr = enriched;
    if (typeFilter !== 'all') arr = arr.filter(r => r.item_type === typeFilter);
    if (streamerFilter)       arr = arr.filter(r => String(r.streamer_id) === String(streamerFilter));
    if (query.trim()) {
      const q = query.toLowerCase();
      arr = arr.filter(r =>
        [r._name, r.pokemon_name, r.card_number, r.set_name, r.card_identified].some(f => (f || '').toLowerCase().includes(q))
      );
    }
    if (sortBy === 'hammer') arr = [...arr].sort((a, b) => (parseFloat(b.hammer_gbp) || 0) - (parseFloat(a.hammer_gbp) || 0));
    else if (sortBy === 'profit') arr = [...arr].sort((a, b) => (b._profit ?? -9999) - (a._profit ?? -9999));
    return arr;
  }, [enriched, typeFilter, streamerFilter, query, sortBy]);

  const summary = useMemo(() => {
    const withCost = results.filter(r => r._profit !== null);
    const rev    = results.reduce((s, r) => s + (parseFloat(r.hammer_gbp) || 0), 0);
    const profit = withCost.reduce((s, r) => s + r._profit, 0);
    const margin = withCost.length ? withCost.reduce((s, r) => s + r._margin, 0) / withCost.length : null;
    return { count: results.length, rev, profit, margin, withCost: withCost.length };
  }, [results]);

  // A filter button; the group around it (segmented or chips) draws it.
  const fBtn = (active, onClick, label) => (
    <button key={label} onClick={onClick} className={active ? 'is-active' : undefined}>{label}</button>
  );

  return (
    <Page width="narrow" className="si-page">
      {modalCard && <CardModal card={modalCard} allCards={results} onClose={() => setModalCard(null)} />}

      <PageHeader title="Sales Search" description="Instant search across all stream sales. Click any card for full details and history." />

      <div className="si-bigsearch">
        <IconSearch size={20} />
        <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
          placeholder={loading
            ? `Loading… ${loadedCount.toLocaleString()} records`
            : `Search ${allData.length.toLocaleString()} sales by name, number, set…`}
        />
        {query && <button onClick={() => setQuery('')} className="si-bigsearch__clear"><IconX size={16} /></button>}
      </div>

      <div className="si-filters">
        <div className="si-filter-group">
          <span className="rc-label">Type</span>
          <div className="rc-segmented">
            {[['slab','Slabs'],['single','Singles'],['all','All']].map(([v,l]) => fBtn(typeFilter===v, ()=>setTypeFilter(v), l))}
          </div>
        </div>
        <div className="si-filter-group si-filter-group--streamers">
          <span className="rc-label">Streamer</span>
          <div className="si-chips">
            {fBtn(!streamerFilter, ()=>setStreamer(null), 'All')}
            {streamerIds.map(id => {
              const active = String(streamerFilter) === String(id);
              return fBtn(active, () => setStreamer(active ? null : id), STREAMERS[id] || `#${id}`);
            })}
          </div>
        </div>
        <div className="si-filter-group si-filter-group--sort">
          <span className="rc-label">Sort</span>
          <div className="rc-segmented">
            {[['recent','Recent'],['hammer','Hammer ↓'],['profit','Profit ↓']].map(([v,l]) => fBtn(sortBy===v, ()=>setSortBy(v), l))}
          </div>
        </div>
      </div>

      {!loading && summary.count > 0 && (
        <div className="si-summary">
          <span><strong>{summary.count.toLocaleString()}</strong> results</span>
          <span>Revenue <strong>£{summary.rev.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</strong></span>
          {summary.withCost > 0 && <>
            <span>Avg profit <strong style={{ color: pCol(summary.profit / summary.withCost) }}>{gbp(summary.profit / summary.withCost, true)}</strong></span>
            <span>Avg margin <strong style={{ color: mCol(summary.margin) }}>{pct(summary.margin)}</strong></span>
          </>}
          {summary.withCost < summary.count && <span className="si-summary__note">{summary.count - summary.withCost} without cost</span>}
        </div>
      )}

      {loading && <div className="si-loading">Loading {loadedCount.toLocaleString()} records…</div>}
      {!loading && results.length === 0 && <div className="si-empty">No results.</div>}

      {!loading && results.length > 0 && (
        <div className="si-table si-table--sales">
          <div className="si-row si-row--head">
            {['Slab','Shot','Card','Streamer','Hammer','Cost','Profit'].map(h => (
              <div key={h} className="rc-label">{h}</div>
            ))}
          </div>
          {results.slice(0, 500).map((r, i) => {
            const grade  = gradeStr(r.grade);
            const sid    = r.streamer_id;
            const profitNeg = r._profit !== null && r._profit < 0;
            return (
              <div key={r.id || i} className={`si-row${profitNeg ? ' is-loss' : ''}`}>
                <div className={`si-cell si-media si-media--slab${r._ace ? ' has-image' : ''}`}>
                  {r._ace ? <img loading="lazy" src={r._ace} alt="" onError={e => e.target.style.opacity = 0} /> : <span className="si-dash">—</span>}
                </div>
                <div className={`si-cell si-media si-media--shot${r._thumb ? ' has-image' : ''}`}>
                  {r._thumb && <img loading="lazy" src={r._thumb} alt="" onError={e => e.target.style.opacity = 0} />}
                </div>
                <div className="si-cell si-card" onClick={() => setModalCard(r)}>
                  <div className="si-card__name">
                    {r._name || <span className="si-unknown">Unknown</span>}
                  </div>
                  <div className="si-meta si-card__meta">
                    {[r.card_number, r.set_name].filter(Boolean).join(' · ')}
                    {grade && <span className="si-grade">{grade}</span>}
                    {r.language === 'Japanese' && <span className="si-flag">🇯🇵</span>}
                  </div>
                  <div className="si-card__date">{fmtDate(r.stream_date)}</div>
                </div>
                <div className="si-cell si-who" data-label="Streamer">
                  <span className="si-who__name">{STREAMERS[sid] || `#${sid}`}</span>
                  <div className="si-muted">{SELLERS[r.seller_username] || r.seller_username}</div>
                </div>
                <div className="si-cell si-num" data-label="Hammer">
                  <div className="si-hammer">£{parseFloat(r.hammer_gbp || 0).toFixed(0)}</div>
                </div>
                <div className="si-cell si-num" data-label="Cost">
                  <div className="si-cost">
                    {r._cost !== null ? `£${parseFloat(r._cost).toFixed(2)}` : <span className="si-dash">—</span>}
                  </div>
                </div>
                <div className="si-cell si-num" data-label="Profit">
                  <div className="si-profit" style={{ color: pCol(r._profit) }}>
                    {r._profit !== null ? gbp(r._profit, true) : <span className="si-dash">—</span>}
                  </div>
                  {r._margin !== null && <div className="si-margin" style={{ color: mCol(r._margin) }}>{pct(r._margin)}</div>}
                </div>
              </div>
            );
          })}
          {results.length > 500 && (
            <div className="si-more">
              Showing 500 of {results.length.toLocaleString()} — refine your search
            </div>
          )}
        </div>
      )}
    </Page>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function StreamIntelligence() {
  const [selected, setSelected] = useState(null);

  return (
    <Page width="narrow" className="si-page">
      {!selected && (
        <>
          <PageHeader title="Stream Intelligence" description="Select a stream to review card-by-card P&L, slab scans and cost data." />
          <StreamList onSelect={setSelected} />
        </>
      )}
      {selected && (
        <StreamDetail stream={selected} onBack={() => setSelected(null)} />
      )}
    </Page>
  );
}
